// frontend/src/pages/RemediationHub.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Sidebar from "../components/Sidebar";
import RemediationFindingCard from "../components/RemediationFindingCard";

import {
  readAuditSnapshotIndex,
  getAuditSnapshotPayload,
  ACTIVE_BIZ_KEY,
} from "../lib/auditHistory";

import {
  readRemediationMap,
  writeRemediationMap,
  cryptoRandomId,
  downloadJson,
} from "../lib/remediationSnapshot";

import {
  getCachedFxRates,
  convertMoney,
  guessCurrencySymbol,
} from "../lib/fxRates";

// ---------------------------
// Constants / storage keys
// ---------------------------
const REMEDIATION_CONTEXT_KEY = "aifine_remediation_context_v1";
const NET_METHOD_KEY = "aifine_net_method_v1"; // strict only (confirmed)
const DEFAULT_NET_METHOD = "STRICT";

// IMPORTANT: support future/biz-scoped index keys
const SNAPSHOT_INDEX_KEY_PREFIX = "aifine_audit_snapshot_index";

// Back-compat: some older flows stored "public audit results"
const PUBLIC_AUDIT_RESULTS_KEY = "aifine_public_audit_results";
const PUBLIC_AUDIT_RESULTS_BIZ_PREFIX = "aifine_public_audit_results_business_";

// Remediation Hub FX preferences (separate from Reports)
const REM_PRIMARY_CCY_KEY = "aifine_remediation_primary_currency_v1";
const REM_FX_MODE_KEY = "aifine_remediation_fx_mode_v1"; // AUDIT_DATE | CURRENT

// ---------------------------
// Small helpers
// ---------------------------
function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safeObj(x) {
  return x && typeof x === "object" && !Array.isArray(x) ? x : {};
}

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function asString(x) {
  return String(x ?? "").trim();
}

function upper(x) {
  return String(x || "").trim().toUpperCase();
}

function normalizeStatus(s) {
  const v = String(s || "OPEN").toUpperCase();
  if (v === "OPEN" || v === "IN_PROGRESS" || v === "MITIGATED" || v === "CLOSED") return v;
  return "OPEN";
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function fmtMoneyWhole(n, symbol) {
  const v = Number(n);
  if (!Number.isFinite(v)) return `${symbol || "$"}0`;
  return `${symbol || "$"}${Math.round(v).toLocaleString("en-US")}`;
}

function tryReadJsonLS(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function tryWriteJsonLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function getActiveBusinessIdSafe() {
  try {
    const v = localStorage.getItem(ACTIVE_BIZ_KEY);
    return v ? String(v) : "";
  } catch {
    return "";
  }
}

function getNetMethodSafe() {
  try {
    const v = localStorage.getItem(NET_METHOD_KEY);
    return (v ? String(v) : DEFAULT_NET_METHOD).toUpperCase();
  } catch {
    return DEFAULT_NET_METHOD;
  }
}

function setNetMethodSafe(v) {
  try {
    localStorage.setItem(NET_METHOD_KEY, String(v || DEFAULT_NET_METHOD).toUpperCase());
  } catch {
    // ignore
  }
}

// ---------------------------
// Snapshot extraction helpers
// ---------------------------

/**
 * Attempt to extract an "audit-date FX cache" embedded in a snapshot payload.
 * We accept a few plausible locations to stay robust.
 *
 * Expected shape (compatible with fxRates.js cache):
 * { base, rates, as_of_iso, source_label, retrieved_at_iso, version }
 */
function extractAuditFxCache(results) {
  const r = safeObj(results);

  // Common candidates (present/future-proof)
  const candidates = [
    r?.fx_cache,
    r?.fxCache,
    r?.meta?.fx_cache,
    r?.meta?.fxCache,
    r?.summary?.fx_cache,
    r?.summary?.fxCache,
    r?.risk_exposure?.fx_cache,
    r?.risk_exposure?.fxCache,
    r?.risk_exposure?.fx,
    r?.risk_exposure?.fx_rates_cache,
  ];

  for (const c of candidates) {
    const o = safeObj(c);
    const rates = o?.rates && typeof o.rates === "object" ? o.rates : null;
    const base = o?.base ? upper(o.base) : "";
    const hasRates = Boolean(rates && Object.keys(rates).length);
    if (hasRates && base) {
      return {
        version: o?.version || o?.cache_version || "fx.snapshot",
        source_label: o?.source_label || o?.source || "Snapshot FX",
        retrieved_at_iso: o?.retrieved_at_iso || o?.retrievedAt || null,
        as_of_iso: o?.as_of_iso || o?.asOf || o?.date || null,
        base,
        rates,
      };
    }
  }

  return null;
}

/**
 * Extract combined_by_currency from v3 risk_exposure if available.
 */
function extractCombinedByCurrency(results) {
  const r = safeObj(results);
  const rx = safeObj(r.risk_exposure);
  const m = rx?.combined_by_currency;
  return m && typeof m === "object" ? m : null;
}

/**
 * Strict baseline (legacy/single-currency fallback):
 * v3 preferred:
 * - results.risk_exposure.headline.combined_structured_worst_case_native
 * - fallback: first bucket in results.risk_exposure.combined_by_currency[<code>].combined_structured_worst_case_native
 *
 * legacy fallback:
 * - results.summary.gross_exposure_ceiling_usd
 * - results.summary.gross_exposure_ceiling
 * - results.summary.gross
 * - results.gross
 */
function extractGrossBaselineSingleCurrency(results) {
  const r = safeObj(results);
  const rx = safeObj(r.risk_exposure);

  const headline = safeObj(rx.headline);
  const headlineGross = Number(headline.combined_structured_worst_case_native);
  if (Number.isFinite(headlineGross) && headlineGross >= 0) return headlineGross;

  const byCcy = safeObj(rx.combined_by_currency);
  const codes = Object.keys(byCcy || {}).filter(Boolean);
  if (codes.length) {
    const b = safeObj(byCcy[codes[0]]);
    const g2 = Number(b.combined_structured_worst_case_native);
    if (Number.isFinite(g2) && g2 >= 0) return g2;
  }

  // legacy fallbacks
  const s = safeObj(r.summary);
  const legacy =
    Number(s.gross_exposure_ceiling_usd) ||
    Number(s.gross_exposure_ceiling) ||
    Number(s.gross) ||
    Number(r.gross) ||
    0;

  return Number.isFinite(legacy) ? legacy : 0;
}

/**
 * Currency symbol (legacy/single-currency fallback):
 * v3 preferred:
 * - results.risk_exposure.headline.currency_symbol
 * - else: bucket currency_symbol
 * - else: results.summary.currency_symbol
 */
function extractCurrencySymbolSingleCurrency(results) {
  const r = safeObj(results);
  const rx = safeObj(r.risk_exposure);
  const headline = safeObj(rx.headline);
  const sym1 = headline.currency_symbol;
  if (sym1) return String(sym1);

  const byCcy = safeObj(rx.combined_by_currency);
  const codes = Object.keys(byCcy || {}).filter(Boolean);
  if (codes.length) {
    const b = safeObj(byCcy[codes[0]]);
    if (b.currency_symbol) return String(b.currency_symbol);
  }

  const s = safeObj(r.summary);
  if (s.currency_symbol) return String(s.currency_symbol);

  return "$";
}

/**
 * Preferred business/entity name:
 * - results.entity_name
 * - results.summary.entity_name
 * - results.config.clientName (older)
 */
function extractBusinessName(results) {
  const r = safeObj(results);
  if (r.entity_name) return String(r.entity_name);
  const s = safeObj(r.summary);
  if (s.entity_name) return String(s.entity_name);
  const cfg = safeObj(r.config);
  if (cfg.clientName) return String(cfg.clientName);
  return "Your Business";
}

/**
 * Findings source:
 * - Prefer results.findings if present (best UX)
 * - Else build placeholder findings from failed/unsure ids so UI still works
 */
function extractFindings(results) {
  const r = safeObj(results);

  const maybe = r.findings;
  if (Array.isArray(maybe) && maybe.length) {
    // ensure each has an id
    return maybe
      .map((f, idx) => {
        const o = safeObj(f);
        return {
          id: o.id ?? `finding_${idx}`,
          kind: o.kind ?? "FAILED",
          category: o.category ?? "—",
          title: o.title ?? o.text ?? "Finding",
          text: o.text ?? o.title ?? "",
          gravity: Number.isFinite(Number(o.gravity)) ? Number(o.gravity) : 5,
          jurisdictions: Array.isArray(o.jurisdictions)
            ? o.jurisdictions
            : Array.isArray(r?.summary?.jurisdictions)
            ? r.summary.jurisdictions
            : [],
          guidance: o.guidance ?? o.playbook ?? "",
        };
      })
      .filter((f) => f && f.id);
  }

  const a = safeObj(r.assessment);
  const failedIds = safeArray(a.failed_question_ids);
  const unsureIds = safeArray(a.unsure_question_ids);

  const out = [];

  failedIds.forEach((qid) => {
    const id = `q_${qid}`;
    out.push({
      id,
      kind: "FAILED",
      category: "Assessment",
      title: `Question ${qid}`,
      text: `Question ${qid} flagged as failed.`,
      gravity: 5,
      jurisdictions: Array.isArray(r?.summary?.jurisdictions) ? r.summary.jurisdictions : [],
      guidance: "",
    });
  });

  unsureIds.forEach((qid) => {
    const id = `q_${qid}`;
    out.push({
      id,
      kind: "UNSURE",
      category: "Assessment",
      title: `Question ${qid}`,
      text: `Question ${qid} marked as unsure.`,
      gravity: 3,
      jurisdictions: Array.isArray(r?.summary?.jurisdictions) ? r.summary.jurisdictions : [],
      guidance: "",
    });
  });

  return out;
}

/**
 * Allocate gross exposure across findings deterministically (STRICT baseline):
 * - weight = max(1, gravity)
 * - each finding gets gross * weight / sumWeights
 */
function allocateExposureByFinding(gross, findings) {
  const list = safeArray(findings);
  const weights = list.map((f) => Math.max(1, Number(f?.gravity) || 1));
  const sumW = weights.reduce((a, b) => a + b, 0) || 1;

  const out = {};
  list.forEach((f, idx) => {
    const id = String(f?.id ?? "");
    if (!id) return;
    const w = weights[idx] || 1;
    out[id] = (Number(gross) || 0) * (w / sumW);
  });
  return out;
}

/**
 * Read the latest “selected snapshot for remediation” context.
 * This is what the Reports button should have set.
 */
function readRemediationContext() {
  const ctx = tryReadJsonLS(REMEDIATION_CONTEXT_KEY, null);
  return ctx && typeof ctx === "object" ? ctx : null;
}

function writeRemediationContext(nextCtx) {
  return tryWriteJsonLS(REMEDIATION_CONTEXT_KEY, nextCtx || {});
}

/**
 * Load audit results in this priority order:
 * 1) snapshot payload from auditHistory (best, immutable)
 * 2) per-business public results (older flow)
 * 3) global public results key (older flow)
 */
function loadAuditResults({ snapshotId, businessId }) {
  const sid = asString(snapshotId);
  if (sid) {
    const p = getAuditSnapshotPayload(sid);
    if (p && typeof p === "object") return p;
  }

  const bid = asString(businessId);
  if (bid) {
    const k = `${PUBLIC_AUDIT_RESULTS_BIZ_PREFIX}${bid}`;
    const p2 = tryReadJsonLS(k, null);
    if (p2 && typeof p2 === "object") return p2;
  }

  const p3 = tryReadJsonLS(PUBLIC_AUDIT_RESULTS_KEY, null);
  if (p3 && typeof p3 === "object") return p3;

  return null;
}

// ---------------------------
// Component
// ---------------------------
export default function RemediationHub() {
  const navigate = useNavigate();

  // mode toggle (UI)
  const [mode, setMode] = useState("EXECUTIVE"); // EXECUTIVE | LEGAL

  // expand/collapse all for playbooks
  const [expandAllTick, setExpandAllTick] = useState(0);
  const [collapseAllTick, setCollapseAllTick] = useState(0);

  // current snapshot context
  const [activeBizId, setActiveBizId] = useState(() => {
    const ctx = readRemediationContext();
    return ctx?.business_id ? String(ctx.business_id) : getActiveBusinessIdSafe();
  });

  const [snapshotId, setSnapshotId] = useState(() => {
    const ctx = readRemediationContext();
    return ctx?.snapshot_id ? String(ctx.snapshot_id) : "";
  });

  // audit results (from snapshot)
  const [results, setResults] = useState(() => {
    const ctx = readRemediationContext();
    const sid = ctx?.snapshot_id ? String(ctx.snapshot_id) : "";
    const bid = ctx?.business_id ? String(ctx.business_id) : getActiveBusinessIdSafe();
    return loadAuditResults({ snapshotId: sid, businessId: bid });
  });

  // remediation map (snapshot-scoped)
  const [remediationMap, setRemediationMap] = useState(() => {
    try {
      const ctx = readRemediationContext();
      const sid = ctx?.snapshot_id ? String(ctx.snapshot_id) : "";
      if (!sid) return {};
      return readRemediationMap({ snapshotId: sid }) || {};
    } catch {
      return {};
    }
  });

  // net method: STRICT only (confirmed)
  const [netMethod, setNetMethod] = useState(() => {
    // enforce STRICT only (no UI drift)
    return "STRICT";
  });

  // ---------------------------
  // FX state (Remediation Hub)
  // ---------------------------

  const [primaryCurrency, setPrimaryCurrency] = useState(() => {
    try {
      return localStorage.getItem(REM_PRIMARY_CCY_KEY) || "";
    } catch {
      return "";
    }
  });

  const [fxMode, setFxMode] = useState(() => {
    try {
      const raw = localStorage.getItem(REM_FX_MODE_KEY) || "AUDIT_DATE";
      const v = upper(raw);
      return v === "CURRENT" ? "CURRENT" : "AUDIT_DATE";
    } catch {
      return "AUDIT_DATE";
    }
  });

  // Live FX cache (Current FX)
  const [fxLive, setFxLive] = useState(() => ({
    status: "idle", // idle | loading | ready | error
    cache: null,
    error: null,
  }));

  // Re-sync when localStorage changes (new audit, new remediation context)
  useEffect(() => {
    const refreshFromStorage = () => {
      const ctx = readRemediationContext();
      const sid = ctx?.snapshot_id ? String(ctx.snapshot_id) : "";

      // Prefer business_id from remediation context (snapshot-bound), else fall back to ACTIVE_BIZ_KEY
      const bid = ctx?.business_id ? String(ctx.business_id) : getActiveBusinessIdSafe();

      setActiveBizId(bid);
      setSnapshotId(sid);

      const nextResults = loadAuditResults({ snapshotId: sid, businessId: bid });
      setResults(nextResults);

      try {
        if (!sid) {
          setRemediationMap({});
        } else {
          const map = readRemediationMap({ snapshotId: sid }) || {};
          setRemediationMap(map);
        }
      } catch {
        setRemediationMap({});
      }

      // strict only
      setNetMethod("STRICT");
      setNetMethodSafe("STRICT");
    };

    refreshFromStorage();

    const onStorage = (e) => {
      if (!e || e.key == null) return;
      const k = String(e.key);

      if (
        k === ACTIVE_BIZ_KEY ||
        k === REMEDIATION_CONTEXT_KEY ||
        k === NET_METHOD_KEY ||
        k.startsWith(SNAPSHOT_INDEX_KEY_PREFIX) ||
        k.startsWith("aifine_audit_snapshot_payload_snap_") ||
        k.startsWith("aifine_public_audit_results") ||
        k.startsWith("aifine_remediation_map_")
      ) {
        refreshFromStorage();
      }
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", refreshFromStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", refreshFromStorage);
    };
  }, []);

  // Persist remediation map whenever it changes
  const writeTimerRef = useRef(null);
  useEffect(() => {
    const sid = asString(snapshotId);
    if (!sid) return;

    // debounce writes a bit (avoid localStorage thrash)
    if (writeTimerRef.current) window.clearTimeout(writeTimerRef.current);
    writeTimerRef.current = window.setTimeout(() => {
      try {
        writeRemediationMap({ snapshotId: sid, map: remediationMap || {} });
      } catch {
        // ignore
      }
    }, 120);

    return () => {
      if (writeTimerRef.current) window.clearTimeout(writeTimerRef.current);
    };
  }, [snapshotId, remediationMap]);

  // Persist FX prefs
  useEffect(() => {
    try {
      if (primaryCurrency) localStorage.setItem(REM_PRIMARY_CCY_KEY, String(primaryCurrency));
    } catch {
      // ignore
    }
  }, [primaryCurrency]);

  useEffect(() => {
    try {
      localStorage.setItem(REM_FX_MODE_KEY, String(fxMode || "AUDIT_DATE"));
    } catch {
      // ignore
    }
  }, [fxMode]);

  // Derived: findings
  const findings = useMemo(() => extractFindings(results), [results]);

  // Multi-currency detection
  const combinedByCurrency = useMemo(() => extractCombinedByCurrency(results), [results]);

  const currencyCodes = useMemo(() => {
    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    return Object.keys(m || {}).filter(Boolean).map(upper).filter(Boolean);
  }, [combinedByCurrency]);

  const isMultiCurrency = currencyCodes.length > 1;

  // Choose primary currency for conversion (Remediation Hub)
  const chosenPrimaryCode = useMemo(() => {
    if (!isMultiCurrency) return currencyCodes[0] || "";
    if (primaryCurrency && currencyCodes.includes(upper(primaryCurrency))) return upper(primaryCurrency);
    // Prefer headline currency_code if present
    const headlineCode = upper(safeObj(safeObj(results)?.risk_exposure)?.headline?.currency_code);
    if (headlineCode && currencyCodes.includes(headlineCode)) return headlineCode;
    return currencyCodes[0] || "";
  }, [isMultiCurrency, currencyCodes, primaryCurrency, results]);

  // Primary symbol (for converted display)
  const primarySymbol = useMemo(() => {
    if (!chosenPrimaryCode) return "$";
    if (!isMultiCurrency) {
      return extractCurrencySymbolSingleCurrency(results);
    }

    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    const b = safeObj(m?.[chosenPrimaryCode]);
    const sym = b?.currency_symbol || safeObj(safeObj(results)?.risk_exposure)?.headline?.currency_symbol || null;
    return sym || guessCurrencySymbol(chosenPrimaryCode) || "$";
  }, [chosenPrimaryCode, isMultiCurrency, results, combinedByCurrency]);

  // Audit-date FX cache (embedded in snapshot, if available)
  const fxAudit = useMemo(() => extractAuditFxCache(results), [results]);
  const auditFxAvailable = Boolean(fxAudit && fxAudit.rates && Object.keys(fxAudit.rates).length);

  // Load live FX when multi-currency (needed for Current FX; also needed as fallback)
  useEffect(() => {
    if (!isMultiCurrency || !chosenPrimaryCode) {
      setFxLive((s) => ({ ...s, status: "idle", error: null }));
      return;
    }

    let alive = true;

    (async () => {
      try {
        setFxLive({ status: "loading", cache: null, error: null });
        const cache = await getCachedFxRates({ maxAgeHours: 12, forceRefresh: false });
        if (!alive) return;
        setFxLive({ status: "ready", cache, error: null });
      } catch (err) {
        if (!alive) return;
        setFxLive({
          status: "error",
          cache: null,
          error: String(err?.message || err || "FX error"),
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, [isMultiCurrency, chosenPrimaryCode]);

  const fxCacheInUse = useMemo(() => {
    if (!isMultiCurrency) return null;
    if (fxMode === "AUDIT_DATE" && auditFxAvailable) return fxAudit;
    if (fxMode === "CURRENT" && fxLive.status === "ready" && fxLive.cache) return fxLive.cache;
    // Fallback: if audit-date missing but we still want conversion, use live if available.
    if (fxLive.status === "ready" && fxLive.cache) return fxLive.cache;
    return null;
  }, [isMultiCurrency, fxMode, auditFxAvailable, fxAudit, fxLive]);

  const fxReadyForConversion = Boolean(isMultiCurrency && chosenPrimaryCode && fxCacheInUse);

  const missingFxFor = useMemo(() => {
    if (!fxReadyForConversion) return [];
    const miss = new Set();
    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    for (const code of currencyCodes) {
      const b = safeObj(m?.[code]);
      const w = Number(b?.combined_structured_worst_case_native);
      if (!Number.isFinite(w)) continue;
      const conv = convertMoney(w, code, chosenPrimaryCode, fxCacheInUse);
      if (conv == null) miss.add(code);
    }
    return Array.from(miss);
  }, [fxReadyForConversion, combinedByCurrency, currencyCodes, chosenPrimaryCode, fxCacheInUse]);

  // Business name
  const businessName = useMemo(() => extractBusinessName(results), [results]);

  // ---------------------------
  // STRICT baseline + allocation
  // ---------------------------

  // Single-currency strict baseline (native)
  const grossBaselineSingle = useMemo(() => extractGrossBaselineSingleCurrency(results), [results]);

  // Multi-currency strict baseline (converted sum -> primary)
  const grossBaseline = useMemo(() => {
    if (!isMultiCurrency) return grossBaselineSingle;

    if (!fxReadyForConversion) return 0;

    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    let total = 0;

    for (const code of currencyCodes) {
      const b = safeObj(m?.[code]);
      const w = Number(b?.combined_structured_worst_case_native);
      if (!Number.isFinite(w)) continue;
      const conv = convertMoney(w, code, chosenPrimaryCode, fxCacheInUse);
      if (typeof conv === "number") total += conv;
    }

    return Math.round(total);
  }, [
    isMultiCurrency,
    grossBaselineSingle,
    fxReadyForConversion,
    combinedByCurrency,
    currencyCodes,
    chosenPrimaryCode,
    fxCacheInUse,
  ]);

  // Allocate gross across findings deterministically
  const allocationById = useMemo(
    () => allocateExposureByFinding(grossBaseline, findings),
    [grossBaseline, findings]
  );

  // Status counts
  const statusCounts = useMemo(() => {
    const counts = { OPEN: 0, IN_PROGRESS: 0, MITIGATED: 0, CLOSED: 0 };
    findings.forEach((f) => {
      const id = String(f?.id ?? "");
      const entry = safeObj(remediationMap?.[id]);
      const st = normalizeStatus(entry?.status || "OPEN");
      counts[st] = (counts[st] || 0) + 1;
    });
    return counts;
  }, [findings, remediationMap]);

  // Status money (STRICT): Remaining by status, CLOSED shows Reduced By
  const statusMoney = useMemo(() => {
    const sums = { OPEN: 0, IN_PROGRESS: 0, MITIGATED: 0, CLOSED: 0 };
    findings.forEach((f) => {
      const id = String(f?.id ?? "");
      const entry = safeObj(remediationMap?.[id]);
      const st = normalizeStatus(entry?.status || "OPEN");
      const amt = Number(allocationById?.[id]) || 0;
      sums[st] = (sums[st] || 0) + amt;
    });

    const reducedBy = sums.CLOSED || 0;
    const remaining = Math.max(0, (Number(grossBaseline) || 0) - reducedBy);

    return {
      sums,
      reducedBy,
      remaining,
    };
  }, [findings, remediationMap, allocationById, grossBaseline]);

  // Page-level “no results” state
  const hasResults = Boolean(results && typeof results === "object");
  const hasFindings = Array.isArray(findings) && findings.length > 0;

  // Handlers (FindingCard integration)
  const handleStatusChange = (findingId, nextStatus) => {
    const fid = String(findingId || "");
    if (!fid) return;

    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      const next = {
        ...entry,
        status: normalizeStatus(nextStatus),
      };
      return { ...cur, [fid]: next };
    });
  };

  const handleNotesChange = (findingId, notes) => {
    const fid = String(findingId || "");
    if (!fid) return;

    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      return { ...cur, [fid]: { ...entry, notes: String(notes || "") } };
    });
  };

  const handleEvidenceAdd = (findingId) => {
    const fid = String(findingId || "");
    if (!fid) return;

    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      const list = safeArray(entry.evidence_links);
      const rid = cryptoRandomId("e");
      const nextList = [...list, { id: rid, label: "", url: "", notes: "" }];
      return { ...cur, [fid]: { ...entry, evidence_links: nextList } };
    });
  };

  const handleEvidenceChange = (findingId, rowId, nextRow) => {
    const fid = String(findingId || "");
    const rid = String(rowId || "");
    if (!fid || !rid) return;

    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      const list = safeArray(entry.evidence_links);
      const nextList = list.map((r) => (String(r?.id) === rid ? nextRow : r));
      return { ...cur, [fid]: { ...entry, evidence_links: nextList } };
    });
  };

  const handleEvidenceRemove = (findingId, rowId) => {
    const fid = String(findingId || "");
    const rid = String(rowId || "");
    if (!fid || !rid) return;

    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      const list = safeArray(entry.evidence_links);
      const nextList = list.filter((r) => String(r?.id) !== rid);
      return { ...cur, [fid]: { ...entry, evidence_links: nextList } };
    });
  };

  const handleUploadAdd = (findingId, uploadMeta) => {
    const fid = String(findingId || "");
    if (!fid) return;

    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      const list = safeArray(entry.uploads);
      const nextList = [...list, safeObj(uploadMeta)];
      return { ...cur, [fid]: { ...entry, uploads: nextList } };
    });
  };

  const handleUploadRemove = (findingId, uploadId) => {
    const fid = String(findingId || "");
    const uid = String(uploadId || "");
    if (!fid || !uid) return;

    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      const list = safeArray(entry.uploads);
      const nextList = list.filter((u) => String(u?.id) !== uid);
      return { ...cur, [fid]: { ...entry, uploads: nextList } };
    });
  };

  // Initialize from previous snapshot (Option A + explicit carry-forward)
  const handleInitializeFromPreviousSnapshot = () => {
    try {
      const ctx = readRemediationContext();
      const bid = ctx?.business_id ? String(ctx.business_id) : getActiveBusinessIdSafe();

      const index = readAuditSnapshotIndex({ businessId: bid }) || [];
      const rows = Array.isArray(index) ? index : [];

      const curSid = String(snapshotId || "");
      if (!curSid) {
        alert("No current snapshot selected for remediation.");
        return;
      }

      // Find previous snapshot for the same business (newest-first list)
      const curIdx = rows.findIndex((r) => String(r?.snapshot_id) === curSid);
      const prevRow = curIdx >= 0 ? rows[curIdx + 1] : rows[1];
      const prevSid = prevRow?.snapshot_id ? String(prevRow.snapshot_id) : "";

      if (!prevSid) {
        alert("No prior snapshot found for this business.");
        return;
      }

      const prevMap = readRemediationMap({ snapshotId: prevSid }) || {};
      writeRemediationMap({ snapshotId: curSid, map: prevMap });

      // update local state immediately
      setRemediationMap(prevMap);

      alert("Initialized remediation state from the previous snapshot.");
    } catch (err) {
      alert(`Initialize failed: ${String(err?.message || err || "Unknown error")}`);
    }
  };

  // Export JSON (snapshot-scoped remediation state)
  const handleExportJson = () => {
    const fx = fxCacheInUse || null;

    const payload = {
      version: "1.1",
      created_at_iso: new Date().toISOString(),
      method: "STRICT",
      snapshot_id: snapshotId || null,
      business_id: activeBizId || null,
      entity_name: businessName,
      currency_code: isMultiCurrency ? chosenPrimaryCode : null,
      currency_symbol: primarySymbol,
      fx_mode: isMultiCurrency ? fxMode : null,
      fx_disclosure: isMultiCurrency
        ? {
            note: "Snapshot is immutable. FX mode affects display conversion only.",
            mode: fxMode,
            primary_currency: chosenPrimaryCode,
            source_label: fx?.source_label || null,
            as_of_iso: fx?.as_of_iso || null,
            retrieved_at_iso: fx?.retrieved_at_iso || null,
            base: fx?.base || null,
            audit_fx_present: auditFxAvailable,
            missing_rates_for: missingFxFor,
          }
        : null,
      gross_baseline: Math.round(Number(grossBaseline) || 0),
      remaining_baseline: Math.round(Number(statusMoney.remaining) || 0),
      remediation_map: remediationMap || {},
    };

    downloadJson("remediation_export", payload);
  };

  // Export PDF: MVP-safe (browser print)
  const handleExportPdf = () => {
    try {
      window.print();
    } catch {
      // ignore
    }
  };

  // Force refresh live FX rates (Current FX)
  const handleUpdateFxRates = async () => {
    if (!isMultiCurrency) return;

    try {
      setFxLive({ status: "loading", cache: null, error: null });
      const cache = await getCachedFxRates({ maxAgeHours: 12, forceRefresh: true });
      setFxLive({ status: "ready", cache, error: null });
    } catch (err) {
      setFxLive({
        status: "error",
        cache: null,
        error: String(err?.message || err || "FX error"),
      });
    }
  };

  // If the page is reached without context, attempt auto-context from latest snapshot
  useEffect(() => {
    if (asString(snapshotId)) return;

    try {
      const bid = getActiveBusinessIdSafe();
      const index = readAuditSnapshotIndex({ businessId: bid }) || [];
      const rows = Array.isArray(index) ? index : [];
      const newest = rows[0]?.snapshot_id ? String(rows[0].snapshot_id) : "";

      if (newest) {
        // Keep compatibility but prefer richer context if available later
        const nextCtx = { snapshot_id: newest, business_id: bid };
        writeRemediationContext(nextCtx);

        setActiveBizId(bid);
        setSnapshotId(newest);

        const nextResults = loadAuditResults({ snapshotId: newest, businessId: bid });
        setResults(nextResults);

        const map = readRemediationMap({ snapshotId: newest }) || {};
        setRemediationMap(map);
      }
    } catch {
      // ignore
    }
  }, [snapshotId]);

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-auto p-8 md:p-12">
        <div className="mx-auto w-full max-w-6xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-3xl font-extrabold tracking-tight">Remediation Hub</div>
              <div className="mt-1 text-lg font-bold text-slate-900">{businessName}</div>
              <div className="mt-2 text-sm text-slate-600 max-w-xl">
                Track remediation progress and remaining exposure. Playbooks adapt by jurisdiction, severity, and mode.
              </div>

              <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-600">
                <span className="rounded-full border bg-white px-3 py-1">
                  Active business: <span className="font-black">{activeBizId || "—"}</span>
                </span>
                <span className="rounded-full border bg-white px-3 py-1">
                  Snapshot: <span className="font-mono font-black">{snapshotId || "—"}</span>
                </span>

                {isMultiCurrency ? (
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-900 font-black">
                    Multi-currency snapshot
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleInitializeFromPreviousSnapshot}
                className="rounded-2xl border bg-white px-4 py-2 text-sm font-bold hover:border-slate-300"
                title="Copy remediation status/notes/evidence from the prior snapshot for this business"
              >
                Initialize from previous snapshot
              </button>

              <button
                type="button"
                onClick={handleExportPdf}
                className="rounded-2xl border bg-white px-4 py-2 text-sm font-bold hover:border-slate-300"
              >
                Export PDF
              </button>

              <button
                type="button"
                onClick={handleExportJson}
                className="rounded-2xl border bg-white px-4 py-2 text-sm font-bold hover:border-slate-300"
              >
                Export JSON
              </button>

              <button
                type="button"
                onClick={() => navigate("/reports")}
                className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800"
              >
                Back to Reports
              </button>
            </div>
          </div>

          {/* Mode toggle */}
          <div className="mt-6 flex items-center justify-end gap-2">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Mode</div>
            <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1">
              <button
                type="button"
                onClick={() => setMode("EXECUTIVE")}
                className={cn(
                  "rounded-xl px-3 py-1.5 text-xs font-black",
                  mode === "EXECUTIVE" ? "bg-slate-900 text-white" : "text-slate-700"
                )}
              >
                Executive
              </button>
              <button
                type="button"
                onClick={() => setMode("LEGAL")}
                className={cn(
                  "rounded-xl px-3 py-1.5 text-xs font-black",
                  mode === "LEGAL" ? "bg-slate-900 text-white" : "text-slate-700"
                )}
              >
                Legal
              </button>
            </div>
          </div>

          {/* Empty state */}
          {!hasResults ? (
            <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-lg font-black">No audit results found.</div>
              <div className="mt-2 text-sm text-slate-600">
                Remediation requires a stored assessment snapshot. Run an Assessment Audit, then use Reports →{" "}
                <span className="font-black">Go to Remediation</span>.
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/assessment")}
                  className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-black text-white hover:bg-slate-800"
                >
                  Go to Assessment →
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/reports")}
                  className="rounded-2xl border bg-white px-4 py-2 text-sm font-bold hover:border-slate-300"
                >
                  Back to Reports
                </button>
              </div>

              <div className="mt-10 pb-2 text-xs text-slate-400">
                © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
              </div>
            </div>
          ) : (
            <>
              {/* FX Controls (only when multi-currency) */}
              {isMultiCurrency ? (
                <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                        FX Conversion (Display Only)
                      </div>
                      <div className="mt-2 text-sm text-slate-700 max-w-3xl">
                        This snapshot is <span className="font-black">immutable</span>. FX settings never change the saved
                        snapshot — they only affect the <span className="font-black">converted totals shown in your selected primary currency</span>.
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded-full border bg-white px-3 py-1">
                          Primary currency: <span className="font-black">{chosenPrimaryCode || "—"}</span>
                        </span>

                        <div className="inline-flex items-center gap-2">
                          <span className="text-slate-600 font-bold">Choose primary currency:</span>
                          <select
                            value={
                              primaryCurrency && currencyCodes.includes(upper(primaryCurrency))
                                ? upper(primaryCurrency)
                                : chosenPrimaryCode
                            }
                            onChange={(e) => setPrimaryCurrency(String(e.target.value || ""))}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black"
                          >
                            {currencyCodes.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* FX Mode pills + update button */}
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                        FX conversion mode
                      </div>

                      <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1">
                        <button
                          type="button"
                          onClick={() => setFxMode("AUDIT_DATE")}
                          className={cn(
                            "rounded-xl px-3 py-1.5 text-xs font-black",
                            fxMode === "AUDIT_DATE" ? "bg-slate-900 text-white" : "text-slate-700"
                          )}
                          title="Default. Uses audit-date FX if stored in the snapshot. If not stored, will fall back to current FX for conversion."
                        >
                          Audit-date FX (default)
                        </button>
                        <button
                          type="button"
                          onClick={() => setFxMode("CURRENT")}
                          className={cn(
                            "rounded-xl px-3 py-1.5 text-xs font-black",
                            fxMode === "CURRENT" ? "bg-slate-900 text-white" : "text-slate-700"
                          )}
                          title="Uses the latest cached FX rates for conversion display. Snapshot remains immutable."
                        >
                          Current FX
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={handleUpdateFxRates}
                        className="rounded-2xl border bg-white px-4 py-2 text-xs font-black hover:bg-slate-50"
                        title="Fetch latest FX rates and update conversion display (does not alter the snapshot)."
                      >
                        Update FX rates
                      </button>
                    </div>
                  </div>

                  {/* FX disclosure */}
                  <div className="mt-4 text-[11px] text-slate-500">
                    <span className="font-black text-slate-700">FX disclosure:</span>{" "}
                    {fxMode === "AUDIT_DATE" ? (
                      auditFxAvailable ? (
                        <>
                          Using <span className="font-black">audit-date FX</span> embedded in this snapshot. Source:{" "}
                          <span className="font-black">{fxAudit?.source_label || "Snapshot FX"}</span>. As of{" "}
                          <span className="font-black">{fmtDateTime(fxAudit?.as_of_iso)}</span>.
                        </>
                      ) : (
                        <>
                          <span className="font-black text-amber-800">Audit-date FX not stored in this snapshot.</span>{" "}
                          Conversions fall back to <span className="font-black">Current FX</span> for display only.
                        </>
                      )
                    ) : fxLive.status === "ready" && fxLive.cache ? (
                      <>
                        Using <span className="font-black">Current FX</span>. Source:{" "}
                        <span className="font-black">{fxLive.cache.source_label}</span>. As of{" "}
                        <span className="font-black">{fmtDateTime(fxLive.cache.as_of_iso)}</span> (retrieved{" "}
                        <span className="font-black">{fmtDateTime(fxLive.cache.retrieved_at_iso)}</span>).
                      </>
                    ) : fxLive.status === "loading" ? (
                      <>Loading current FX rates…</>
                    ) : (
                      <>
                        <span className="font-black text-amber-800">Current FX unavailable.</span> Conversions may be incomplete.
                      </>
                    )}
                    {" "}
                    <span className="text-slate-400">
                      Snapshot is immutable; FX only affects converted display totals in {chosenPrimaryCode || "primary currency"}.
                    </span>
                    {missingFxFor?.length ? (
                      <>
                        {" "}
                        <span className="text-amber-700 font-black">Note:</span> Missing FX rates for:{" "}
                        <span className="font-black">{missingFxFor.join(", ")}</span>. Those currencies are excluded from converted totals.
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {/* Exposure hero */}
              <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                  Potential Maximum Exposure → Remaining Exposure
                </div>

                <div className="mt-3 flex items-end gap-3">
                  <div className="text-4xl font-black text-red-700">
                    {fmtMoneyWhole(grossBaseline, primarySymbol)}
                  </div>
                  <div className="text-4xl font-black text-slate-300">→</div>
                  <div className="text-4xl font-black text-emerald-700">
                    {fmtMoneyWhole(statusMoney.remaining, primarySymbol)}
                  </div>
                </div>

                <div className="mt-2 text-xs text-slate-500">
                  STRICT baseline. Closed items reduce remaining exposure; other statuses show remaining by category.
                  {isMultiCurrency ? (
                    <>
                      {" "}
                      Converted display in <span className="font-black">{chosenPrimaryCode}</span> (snapshot remains immutable).
                    </>
                  ) : null}
                </div>
              </div>

              {/* Status Overview */}
              <div className="mt-10">
                <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Status Overview</div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="rounded-3xl border border-red-400 bg-white p-5">
                    <div className="text-xs font-black uppercase tracking-wide text-slate-900">Open</div>
                    <div className="mt-2 text-3xl font-black">{statusCounts.OPEN || 0}</div>
                    <div className="mt-1 text-xs text-slate-600">
                      Remaining:{" "}
                      <span className="font-black">{fmtMoneyWhole(statusMoney.sums.OPEN, primarySymbol)}</span>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-amber-400 bg-white p-5">
                    <div className="text-xs font-black uppercase tracking-wide text-slate-900">In Progress</div>
                    <div className="mt-2 text-3xl font-black">{statusCounts.IN_PROGRESS || 0}</div>
                    <div className="mt-1 text-xs text-slate-600">
                      Remaining:{" "}
                      <span className="font-black">{fmtMoneyWhole(statusMoney.sums.IN_PROGRESS, primarySymbol)}</span>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-blue-400 bg-white p-5">
                    <div className="text-xs font-black uppercase tracking-wide text-slate-900">Mitigated</div>
                    <div className="mt-2 text-3xl font-black">{statusCounts.MITIGATED || 0}</div>
                    <div className="mt-1 text-xs text-slate-600">
                      Remaining:{" "}
                      <span className="font-black">{fmtMoneyWhole(statusMoney.sums.MITIGATED, primarySymbol)}</span>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-emerald-500 bg-white p-5">
                    <div className="text-xs font-black uppercase tracking-wide text-slate-900">Closed</div>
                    <div className="mt-2 text-3xl font-black">{statusCounts.CLOSED || 0}</div>
                    <div className="mt-1 text-xs text-slate-600">
                      Reduced By:{" "}
                      <span className="font-black">{fmtMoneyWhole(statusMoney.reducedBy, primarySymbol)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                  Findings Controls
                </div>
                <div className="mt-2 text-sm text-slate-600">
                  Filter by status and sort to focus remediation efficiently.
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs text-slate-500">
                    Showing: <span className="font-black">{findings.length}</span> finding(s)
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setExpandAllTick((v) => v + 1)}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-slate-300"
                    >
                      Expand all
                    </button>
                    <button
                      type="button"
                      onClick={() => setCollapseAllTick((v) => v + 1)}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-slate-300"
                    >
                      Collapse all
                    </button>
                  </div>
                </div>
              </div>

              {/* Evidence guidance */}
              <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Evidence Guidance</div>
                <div className="mt-2 text-sm text-slate-600">
                  Add evidence links and upload metadata to support defensibility. Files are currently stored as{" "}
                  <span className="font-black">upload-ready metadata</span> until database storage is enabled.
                </div>
              </div>

              {/* Findings list */}
              <div className="mt-8 space-y-4">
                {!hasFindings ? (
                  <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm text-sm text-slate-600">
                    No findings found for this snapshot.
                  </div>
                ) : (
                  findings.map((finding, idx) => {
                    const fid = String(finding?.id ?? "");
                    const entry = safeObj(remediationMap?.[fid]);

                    const defaultExpandLegalDetails =
                      String(mode).toUpperCase() === "LEGAL" && idx < 3 && Number(finding?.gravity || 0) >= 7;

                    return (
                      <RemediationFindingCard
                        key={fid || idx}
                        finding={finding}
                        entry={entry}
                        statuses={[
                          { value: "OPEN", label: "Open" },
                          { value: "IN_PROGRESS", label: "In Progress" },
                          { value: "MITIGATED", label: "Mitigated" },
                          { value: "CLOSED", label: "Closed" },
                        ]}
                        mode={String(mode).toUpperCase()}
                        onStatusChange={handleStatusChange}
                        onNotesChange={handleNotesChange}
                        onEvidenceAdd={handleEvidenceAdd}
                        onEvidenceChange={handleEvidenceChange}
                        onEvidenceRemove={handleEvidenceRemove}
                        onUploadAdd={handleUploadAdd}
                        onUploadRemove={handleUploadRemove}
                        layoutVariant="ROW_C_FULL_PLAYBOOK"
                        expandAllTick={expandAllTick}
                        collapseAllTick={collapseAllTick}
                        defaultExpandLegalDetails={defaultExpandLegalDetails}
                      />
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div className="mt-10 pb-8 text-xs text-slate-400">
                © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}